import { ScreenReaderContent, Text } from "@instructure/ui";
import { useTranslation } from "react-i18next";
import { useContext, useEffect, useState } from "react";
import DataGrid from "react-data-grid";
import {
  MasteryLevelContext,
  TermsContext,
} from "../../../../../utils/Context";
import { TruncateToolTip } from "../../../../../utils/TruncateTooltip";
import { sortOutcomes } from "../../../../LearningOutcomes/utils";
import RatingDescriptionModal from "../RatingDescriptionModal";
import RenderDescription from "./RenderDescription";
import RenderSLOContent from "./RenderSLOContent";
import RenderSubmitContent from "./RenderSubmitContent";

const AssignmentRubric = ({
  assessment,
  outcomes,
  masteryMapping,
  deployed,
  assignmentRubric,
  setAssignmentRubric,
}) => {
  const { t } = useTranslation();
  const { masteryLevels, ratingScales } = useContext(MasteryLevelContext);
  const { canvasTerms, insightsTerms } = useContext(TermsContext);
  const [rows, setRows] = useState([]);
  const [cols, setCols] = useState([]);
  const [openDescModal, setOpenDescModal] = useState(false);
  const [descData, setDescData] = useState({
    description: "",
    masteryLevel: "",
    row: {},
    column: {},
  });

  const ratingToMasteryLevelMap = ratingScales.find(
    (scale) => scale.id === masteryMapping
  );
  const checkEditable = () => {
    if (assessment.id && assessment.assessment_type === "external") {
      const canvasTerm = canvasTerms.find(
        (term) => term.id === assessment.assessing_terms[0]
      );
      const insightsTerm = insightsTerms.find(
        (term) => term.id === canvasTerm.insights_term_id
      );
      if (new Date(insightsTerm.start_at) < new Date()) {
        return false;
      }
    }
    return true;
  };
  const editable = checkEditable();

  const initializeRows = () => {
    const rowObj = [{ id: "raterRow", ratingLevels: {} }];
    sortOutcomes(outcomes).forEach((outcome) => {
      rowObj.push({
        slo: outcome.name + ": " + outcome.statement,
        id: outcome.id,
        ratingLevels: {},
        disabled: false,
      });
    });
    return rowObj;
  };

  const calculateColumnSpan = (level, ratingToMasteryLevelMap) => {
    let count = 0;
    ratingToMasteryLevelMap.levels.forEach((lvl) => {
      if (lvl.mastery_level_id === level.mastery_level_id) {
        count++;
      }
    });
    return count;
  };

  const addRatingLevelColumn = (level, ratingToMasteryLevelMap) => {
    const masteryLevel = masteryLevels.find(
      (lvl) => lvl.id === level.mastery_level_id
    );
    const column = {
      id: level.mastery_level_id,
      insights_rating_level: level.id,
      name: (
        <div
          style={{
            textAlign: "center",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            overflow: "hidden",
          }}
        >
          <TruncateToolTip
            renderTip={
              <div style={{ maxWidth: 400 }}>{masteryLevel.level}</div>
            }
            maxLines={1}
          >
            <Text size="x-small">{masteryLevel.level}</Text>
          </TruncateToolTip>
        </div>
      ),
      colSpan(args) {
        if (args.type === "HEADER") {
          return calculateColumnSpan(level, ratingToMasteryLevelMap);
        }
      },
      renderCell: ({ row, column }) => (
        <RenderDescription
          row={row}
          column={column}
          editable={!deployed && editable}
        />
      ),
    };
    column["key"] = level.id;
    column["score"] = parseInt(level.points);
    return column;
  };

  const fillAssignmentRubricDescription = (outcome, level) => {
    let rating = {};
    const scale = assignmentRubric.rating_scales.find(
      (s) => outcome.id === s.criteria_id
    );
    scale.rating_levels.forEach((lvl) => {
      if (
        lvl.insights_rating_level &&
        (lvl.insights_rating_level === level.id ||
          lvl.insights_rating_level.id === level.id)
      ) {
        rating = lvl;
      }
    });
    return rating ? rating.description : t("ratingScale.outcomeCorrupt");
  };

  useEffect(() => {
    const colObj = [];
    const rowObj = initializeRows();

    ratingToMasteryLevelMap.levels.forEach((level) => {
      colObj.push(addRatingLevelColumn(level, ratingToMasteryLevelMap));
      rowObj.forEach((row, index) => {
        if (index === 0) {
          // Add rating scale point values to header row
          row.ratingLevels[level.id] = level.points;
        } else {
          const outcome = outcomes.find((o) => o.id === row.id);
          if (
            assignmentRubric &&
            assignmentRubric.rating_scales[0].rating_to_mastery_level_map ===
              ratingToMasteryLevelMap.id &&
            assignmentRubric.rating_scales.find(
              (rs) => rs.criteria_id === row.id
            )
          ) {
            // Case 1: Fill descriptions from the assignment rubric
            row.ratingLevels[level.id] = fillAssignmentRubricDescription(
              outcome,
              level
            );
            row.save = false;
          } else {
            // Case 2: Fill descriptions from outcome rating scales
            const scale = outcome.rating_scales.find(
              (s) =>
                s.rating_to_mastery_level_map === ratingToMasteryLevelMap.id
            );
            if (scale) {
              const rating = scale.rating_levels.find(
                (r) =>
                  r.insights_rating_level === level.id ||
                  r.insights_rating_level.id === level.id
              );
              row.ratingLevels[level.id] = rating.description;
            } else {
              // Case 3: Fill descriptions from mastery level mapping
              row.ratingLevels[level.id] = level.description;
            }
            row.save = true;
          }
        }
      });
    });
    // Sort columns for rating scale number and add the first column (list of LOs)
    colObj.sort((a, b) => b.score - a.score);
    colObj.unshift({
      key: "slo",
      name: (
        <ScreenReaderContent>
          {t("ratingScale.sloColumnScreenReader")}
        </ScreenReaderContent>
      ),
      renderCell: ({ row, column }) => (
        <RenderSLOContent row={row} column={column} />
      ),
    });
    if (!deployed && editable) {
      colObj.push({
        key: "submitCriteria",
        name: (
          <ScreenReaderContent>
            {t("ratingScale.submitCriteriaScreenReader")}
          </ScreenReaderContent>
        ),
        renderCell: ({ row }) =>
          row.id !== "raterRow" && (
            <RenderSubmitContent
              row={row}
              setRows={setRows}
              cols={colObj}
              ratingToMasteryLevelMap={ratingToMasteryLevelMap}
              assignmentRubric={assignmentRubric}
              setAssignmentRubric={setAssignmentRubric}
              rebuildSubmitColumn={rebuildSubmitColumn}
            />
          ),
      });
    }
    setCols(colObj);
    setRows(rowObj);
  }, [ratingToMasteryLevelMap, outcomes, editable]);

  const rebuildSubmitColumn = (oldCols, assignmentRubric) => {
    if (!deployed) {
      const newCols = oldCols.slice(0, -1);
      newCols.push({
        key: "submitCriteria",
        name: (
          <ScreenReaderContent>
            {t("ratingScale.submitCriteriaScreenReader")}
          </ScreenReaderContent>
        ),
        renderCell: ({ row }) =>
          row.id !== "raterRow" && (
            <RenderSubmitContent
              row={row}
              setRows={setRows}
              cols={newCols}
              ratingToMasteryLevelMap={ratingToMasteryLevelMap}
              assignmentRubric={assignmentRubric}
              setAssignmentRubric={setAssignmentRubric}
              rebuildSubmitColumn={rebuildSubmitColumn}
            />
          ),
      });
      setCols(newCols);
    }
  };

  const handleSaveDesc = (newDesc) => {
    const newRows = JSON.parse(JSON.stringify(rows));
    const rowIndex = newRows.findIndex((r) => r.id === descData.row.id);
    newRows[rowIndex].ratingLevels[descData.column.key] = newDesc;
    setRows(newRows);
    setOpenDescModal(false);
  };

  const handleCellClick = (args) => {
    const row = args.row;
    const column = args.column;
    if (
      editable &&
      row.id !== "raterRow" &&
      column.key !== "slo" &&
      column.key !== "submitCriteria"
    ) {
      setOpenDescModal(true);
      setDescData({
        description: row.ratingLevels[column.key],
        masteryLevel: column.name.props.children.props.children.props.children,
        row: row,
        column: column,
      });
    }
  };

  const handleCellKeyDown = (args, e) => {
    if (e.key === "Enter") {
      handleCellClick(args);
    }
  };

  const rowHeight = (row) => {
    if (row.id === "raterRow") {
      return 35;
    } else {
      return 100;
    }
  };

  return (
    cols.length > 0 &&
    rows.length > 0 && (
      <>
        <DataGrid
          key={cols.length}
          className="rdg-light"
          style={{ height: "auto" }}
          columns={cols}
          rows={rows}
          rowHeight={rowHeight}
          headerRowHeight={40}
          onCellClick={handleCellClick}
          onCellKeyDown={handleCellKeyDown}
          defaultColumnOptions={{ width: "minmax(0px, auto)" }}
        />
        <RatingDescriptionModal
          openModal={openDescModal}
          setOpenModal={setOpenDescModal}
          descData={descData}
          handleSaveDesc={handleSaveDesc}
        />
      </>
    )
  );
};

export default AssignmentRubric;
